import { useCallback, useMemo } from 'react';
import {
  useCreateConsensusRule,
  useUpdateConsensusRule,
} from '@cbhq/identity-api-hooks/account-management/mutation';

import type { ApprovalRule } from './approvalRulesDisplay';
import { decodeApproverRoleValue } from './approvalRulesDisplay';
import { DEFAULT_CONSENSUS_2FA_TYPE } from './constants';

type SaveApprovalRuleParams = {
  rule: Pick<ApprovalRule, 'action' | 'product' | 'resourceType' | 'target'>;
  ruleName: string;
  isEnabled: boolean;
  approvers: string[];
  requirementCount: number;
  eligibleApproverCount: number;
};

export function useApprovalRulesMutations() {
  const createConsensusRule = useCreateConsensusRule();
  const updateConsensusRule = useUpdateConsensusRule();

  const saveApprovalRule = useCallback(
    async ({
      rule,
      ruleName,
      isEnabled,
      approvers,
      requirementCount,
      eligibleApproverCount,
    }: SaveApprovalRuleParams) => {
      if (
        isEnabled &&
        (requirementCount < 1 || approvers.length === 0 || requirementCount > eligibleApproverCount)
      ) {
        throw new Error('Approval rule configuration is invalid.');
      }

      const approverRoles = approvers.map(decodeApproverRoleValue);
      const { entityId, ruleId, version } = rule.target;

      if (isEnabled && !entityId) {
        throw new Error('No legal entity selected to save the approval rule.');
      }

      if (ruleId && !version) {
        throw new Error('Consensus rule version is required to update an approval rule.');
      }

      // Disabling only affects an entity that already persisted the rule; otherwise it is already off.
      if (!isEnabled && !ruleId) {
        return undefined;
      }

      const result = !ruleId
        ? await createConsensusRule(
            { entityId },
            {
              product: rule.product,
              resourceType: rule.resourceType,
              resourceId: '*',
              action: rule.action,
              requiredApprovals: requirementCount,
              consensus2faType: DEFAULT_CONSENSUS_2FA_TYPE,
              ruleName,
              approverRoles,
            },
          )
        : await updateConsensusRule(
            { entityId, ruleId },
            {
              version,
              enabledStatus: isEnabled ? 'active' : 'inactive',
              approverRoles,
              requiredApprovals: requirementCount,
            },
          );

      // The generated mutations resolve ApiError/NetworkError (both Error subclasses) as values
      // instead of rejecting, so any Error here counts as a failure.
      if (result instanceof Error) {
        throw result;
      }

      return result;
    },
    [createConsensusRule, updateConsensusRule],
  );

  return useMemo(() => ({ saveApprovalRule }), [saveApprovalRule]);
}
